import { Button, Icon } from "@elit-hair/elit-ui";
import { useQueries } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { Fragment, useCallback, useMemo, useState } from "react";
import { TbArrowLeft, TbFileSpreadsheet, TbLoader2 } from "react-icons/tb";
import { toast } from "sonner";
import { z } from "zod";

import { Link as AppLink } from "@/components/link";
import { getTeamMembersKpiByDateRange } from "@/hooks/queries/call.queries";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { ReportsWeekSelect } from "@/pages/performance/reports/components/reports-week-select";
import { getLastCompletedWeekAnchor } from "@/pages/performance/reports/utils/get-last-completed-week-anchor";
import type { KpiData, TeamMembersKpiResponse } from "@/types/call.types";
import { getDateString } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import type { KpisReportExportRow } from "./types/kpis-report.types";
import { exportKpisReportToExcel } from "./utils/export-kpis-report";

const KpisReportSearchSchema = z.object({
    weekOf: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_KPIS_REPORT)({
    component: KpisReportPage,
    validateSearch: searchValidator(KpisReportSearchSchema),
});

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function resolveSelectedWeek(weekOf: string | undefined): DateTime {
    if (!weekOf) {
        return getLastCompletedWeekAnchor();
    }

    const parsed = DateTime.fromISO(weekOf);
    if (!parsed.isValid) {
        return getLastCompletedWeekAnchor();
    }

    return parsed.startOf("week");
}

function getWeekDays(weekStart: DateTime): string[] {
    return Array.from({ length: 7 }, (_, i) => getDateString(weekStart.plus({ days: i })));
}

const PILL_ALL = "bg-slate-200/90 text-slate-800 dark:bg-slate-600/50 dark:text-slate-200";
const PILL_BRUTTOS = "bg-amber-200/90 text-amber-900 dark:bg-amber-600/50 dark:text-amber-100";
const PILL_NETTOS = "bg-emerald-200/90 text-emerald-900 dark:bg-emerald-600/50 dark:text-emerald-100";

function KpiCell({ kpi }: { kpi: KpiData | null }) {
    if (!kpi) {
        return <span className="flex h-full min-h-[2rem] items-center justify-center text-muted-foreground">—</span>;
    }
    return (
        <div className="flex h-full min-h-[2rem] w-full">
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_ALL}`}
                title="All"
            >
                {kpi.all}
            </span>
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_BRUTTOS}`}
                title="Bruttos"
            >
                {kpi.bruttos}
            </span>
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_NETTOS}`}
                title="Nettos"
            >
                {kpi.netto}
            </span>
        </div>
    );
}

function averageKpi(kpis: (KpiData | null)[]): KpiData {
    const n = kpis.length || 1;
    const sum = (get: (k: KpiData) => number) => kpis.reduce((s, k) => s + (k ? get(k) : 0), 0);
    return {
        all: Math.round(sum((k) => k.all) / n),
        bruttos: Math.round(sum((k) => k.bruttos) / n),
        netto: Math.round(sum((k) => k.netto) / n),
        averageCallDuration: sum((k) => k.averageCallDuration) / n,
        inCallTime: sum((k) => k.inCallTime) / n,
        callsPerHour: {},
    };
}

function sumKpi(kpis: (KpiData | null)[]): KpiData | null {
    const present = kpis.filter((kpi): kpi is KpiData => kpi != null);
    if (present.length === 0) {
        return null;
    }

    const all = present.reduce((total, kpi) => total + kpi.all, 0);
    const bruttos = present.reduce((total, kpi) => total + kpi.bruttos, 0);
    const netto = present.reduce((total, kpi) => total + kpi.netto, 0);
    const inCallTime = present.reduce((total, kpi) => total + kpi.inCallTime, 0);

    return {
        all,
        bruttos,
        netto,
        inCallTime,
        averageCallDuration: all > 0 ? inCallTime / all : 0,
        callsPerHour: {},
    };
}

function KpisReportPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const weekStart = useMemo(() => resolveSelectedWeek(search.weekOf), [search.weekOf]);
    const weekEnd = useMemo(() => weekStart.endOf("week").startOf("day"), [weekStart]);
    const days = useMemo(() => getWeekDays(weekStart), [weekStart]);

    const { data: teamsData } = useTeams();
    const teams = teamsData?.data ?? [];

    const teamQueriesConfig = useMemo(
        () =>
            teams.map((team) => ({
                queryKey: ["teamMembersKpiByDateRange", weekStart.toISODate(), weekEnd.toISODate(), team.id],
                queryFn: () => getTeamMembersKpiByDateRange(weekStart, weekEnd, team.id),
                enabled: teams.length > 0,
            })),
        [teams, weekStart, weekEnd]
    );

    const teamQueries = useQueries({ queries: teamQueriesConfig });
    const isLoadingKpis = teamQueries.some((query) => query.isLoading);
    const [isExporting, setIsExporting] = useState(false);

    const teamKpisByDate = useMemo(() => {
        const result: Record<string, Record<string, Record<string, KpiData>>> = {};
        teams.forEach((team, idx) => {
            const response: Record<string, TeamMembersKpiResponse> | undefined = teamQueries[idx]?.data;
            if (!response) return;
            result[team.id] = {};
            days.forEach((dateKey) => {
                const dayData = response[dateKey];
                if (!dayData?.members) return;
                result[team.id][dateKey] = {};
                dayData.members.forEach((m) => {
                    result[team.id][dateKey][m.userId] = m.kpiData;
                });
            });
        });
        return result;
    }, [teamQueries, teams, days]);

    const reportRows = useMemo(() => {
        const rows: KpisReportExportRow[] = [];

        teams.forEach((team) => {
            const byDate = teamKpisByDate[team.id];
            const memberIds = team.members.map((m) => m.id);

            memberIds.forEach((userId) => {
                const kpisByDay = days.map((dateKey) => byDate?.[dateKey]?.[userId] ?? null);
                rows.push({
                    name: team.members.find((m) => m.id === userId)?.user?.name ?? userId,
                    teamId: team.id,
                    teamName: team.name,
                    rowType: "agent",
                    kpisByDay,
                    average: averageKpi(kpisByDay),
                });
            });

            const teamSumByDay = days.map((dateKey) => {
                const dayKpis = memberIds.map((uid) => byDate?.[dateKey]?.[uid] ?? null);
                return sumKpi(dayKpis);
            });

            const teamAvgByDay = days.map((dateKey) => {
                const dayKpis = memberIds
                    .map((uid) => byDate?.[dateKey]?.[uid] ?? null)
                    .filter((kpi): kpi is KpiData => kpi != null);
                return dayKpis.length ? averageKpi(dayKpis) : null;
            });

            rows.push({
                name: `${team.name} · Sum`,
                teamId: team.id,
                teamName: team.name,
                rowType: "team_sum",
                kpisByDay: teamSumByDay,
                average: averageKpi(teamSumByDay),
            });

            rows.push({
                name: `${team.name} · Avg`,
                teamId: team.id,
                teamName: team.name,
                rowType: "team_avg",
                kpisByDay: teamAvgByDay,
                average: averageKpi(teamAvgByDay),
            });
        });

        return rows;
    }, [teams, teamKpisByDate, days]);

    const handleWeekChange = useCallback(
        (date: DateTime) => {
            void navigate({
                search: {
                    weekOf: date.startOf("week").toISODate() ?? undefined,
                },
                replace: true,
            });
        },
        [navigate]
    );

    const handleExportToExcel = useCallback(async () => {
        if (reportRows.length === 0) {
            return;
        }

        setIsExporting(true);
        try {
            await exportKpisReportToExcel({
                weekStart: getDateString(weekStart),
                weekEnd: getDateString(weekEnd),
                days,
                rows: reportRows,
            });
            toast.success("Export successful");
        } catch {
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    }, [reportRows, weekStart, weekEnd, days]);

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center gap-2">
                <AppLink
                    to={APP_ROUTES_TO.DEVELOPER}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <Icon icon={TbArrowLeft} /> Back to experiments
                </AppLink>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">KPIs report</h1>
                    <p className="text-muted-foreground">
                        Weekly KPI table by team. All | Bruttos | Netto per day. Each team has Sum and Avg rows; last
                        column is the week average.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleExportToExcel}
                        disabled={isExporting || isLoadingKpis || reportRows.length === 0}
                    >
                        <Icon
                            icon={isExporting ? TbLoader2 : TbFileSpreadsheet}
                            className={isExporting ? "animate-spin" : undefined}
                        />
                        Export to Excel
                    </Button>
                    <div className="min-w-[20rem]">
                        <ReportsWeekSelect selectedDate={weekStart} onDateChange={handleWeekChange} />
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded-md border border-b-0 bg-muted/30 px-3 py-2 text-xs">
                <span className="text-muted-foreground">Per cell:</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_ALL}`}>All</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_BRUTTOS}`}>Bruttos</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_NETTOS}`}>Nettos</span>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[800px] border-collapse text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="border-r px-3 py-2 text-left font-medium">Agent / Team</th>
                            {days.map((d, i) => (
                                <th key={d} className="border-r px-2 py-2 text-center font-medium">
                                    {WEEKDAY_LABELS[i]}
                                </th>
                            ))}
                            <th className="px-3 py-2 text-center font-medium">Average</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teams.map((team) => {
                            const teamRows = reportRows.filter((row) => row.teamId === team.id);

                            return (
                                <Fragment key={team.id}>
                                    {teamRows.map((row) => {
                                        const isTeamRow = row.rowType !== "agent";
                                        const isLastTeamRow = row.rowType === "team_avg";

                                        return (
                                            <tr
                                                key={`${team.id}-${row.rowType}-${row.name}`}
                                                className={
                                                    isTeamRow
                                                        ? `bg-muted/40 font-medium ${isLastTeamRow ? "border-b-2" : "border-b"}`
                                                        : "border-b hover:bg-muted/30"
                                                }
                                                style={isLastTeamRow ? { borderBottomColor: team.color } : undefined}
                                            >
                                                <td
                                                    className="border-r border-l-4 px-3 py-1.5 font-medium"
                                                    style={{ borderLeftColor: team.color }}
                                                >
                                                    {isTeamRow ? (
                                                        <span className="flex items-center gap-2">
                                                            <span
                                                                className="h-2 w-2 shrink-0 rounded-full"
                                                                style={{ backgroundColor: team.color }}
                                                            />
                                                            {row.name}
                                                        </span>
                                                    ) : (
                                                        row.name
                                                    )}
                                                </td>
                                                {row.kpisByDay.map((kpi, di) => (
                                                    <td
                                                        key={days[di]}
                                                        className="w-[1%] min-w-[6rem] border-r p-0 align-middle"
                                                    >
                                                        <KpiCell kpi={kpi} />
                                                    </td>
                                                ))}
                                                <td
                                                    className={`w-[1%] min-w-[6rem] p-0 align-middle ${isTeamRow ? "" : "font-medium"}`}
                                                >
                                                    <KpiCell kpi={row.average} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {teams.length === 0 && (
                <p className="text-center text-muted-foreground">No teams found. Create teams and add members first.</p>
            )}
        </div>
    );
}
